import { ChevronDown } from "@untitledui/icons";
import { Button as AriaButton, MenuItem as AriaMenuItem, Text as AriaText } from "react-aria-components";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { RadioButtonBase } from "@/components/base/radio-buttons/radio-buttons";
import { cx } from "@/utils/cx";
import {
  currencyName,
  currencySymbol,
  isTotalsCurrency,
  TOTALS_CURRENCIES,
  type TotalsCurrency,
} from "../lib/currency";
import { regionCode } from "../lib/regions";

type TotalsCurrencyPickerProps = {
  selected: TotalsCurrency;
  local: { currency: string; regions: string[] } | null;
  onChange: (currency: TotalsCurrency) => void;
};

const TOTALS_NOTES = [
  "Combines merchants that sell in different currencies into one total.",
  "Converted at daily reference rates. Both periods use the same rate, so changes reflect sales, not exchange rates.",
];

const localNotes = ({ currency, regions }: { currency: string; regions: string[] }) => [
  `You're viewing ${regions.map(regionCode).join(", ")}, so totals are in ${currency}.`,
  "Merchants are shown in their local currency.",
];

export function TotalsCurrencyPicker({ selected, local, onChange }: TotalsCurrencyPickerProps) {
  const shown = local?.currency ?? selected;
  const heading = local ? "Shown in" : "Show totals in";
  const options: string[] =
    local && !isTotalsCurrency(local.currency) ? [local.currency, ...TOTALS_CURRENCIES] : [...TOTALS_CURRENCIES];
  const disabled = local ? options.filter((currency) => currency !== local.currency) : [];
  const notes = local ? localNotes(local) : TOTALS_NOTES;

  return (
    <Dropdown.Root>
      <AriaButton className="group inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-[#21133a]/70 outline-focus-ring hover:text-[#21133a] focus-visible:outline-2">
        <span>
          {heading} <strong className="font-semibold text-[#21133a]">{`${currencySymbol(shown)} ${shown}`}</strong>
        </span>
        <ChevronDown
          width={14}
          height={14}
          aria-hidden="true"
          className="transition-transform duration-150 group-aria-expanded:rotate-180"
        />
      </AriaButton>
      <Dropdown.Popover placement="bottom start" className="w-[290px] p-2">
        <p className="px-2.5 pt-1.5 pb-1 text-[13px] font-semibold text-[#21133a]">{heading}</p>
        <Dropdown.Menu
          selectionMode="single"
          selectedKeys={new Set([shown])}
          disabledKeys={disabled}
          onSelectionChange={(keys) => {
            const [key] = keys === "all" ? [] : [...keys].map(String);
            if (key && isTotalsCurrency(key)) onChange(key);
          }}
          className="py-0"
        >
          {options.map((currency) => (
            <AriaMenuItem
              key={currency}
              id={currency}
              textValue={currencyName(currency)}
              className={(state) =>
                cx(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-[#21133a] outline-hidden",
                  state.isDisabled ? "cursor-default opacity-45" : "cursor-pointer",
                  state.isFocused && "bg-primary_hover",
                )
              }
            >
              {(state) => (
                <>
                  <RadioButtonBase
                    isSelected={state.isSelected}
                    isDisabled={state.isDisabled}
                    isFocusVisible={state.isFocusVisible}
                  />
                  <AriaText slot="label" className="min-w-0 flex-1">
                    {`${currencyName(currency)} (${currencySymbol(currency)})`}
                  </AriaText>
                </>
              )}
            </AriaMenuItem>
          ))}
        </Dropdown.Menu>
        <div className="mt-1 flex flex-col gap-1.5 border-t border-[#21133a]/8 px-2.5 pt-2.5 pb-1.5">
          {notes.map((note) => (
            <p key={note} className="text-xs text-[#21133a]/60">
              {note}
            </p>
          ))}
        </div>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
